import {
  TableToolbarConfig,
  ColumnConfig,
  RowConfig,
  ToolbarSelectionContext,
} from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { Button } from '@/modules/Button';
import { Input } from '@/modules/Input';
import { useState, useEffect, useRef, useMemo } from 'react';
import { InnerButton } from '@/modules/InnerButton';
import { Popup } from '@/modules/Popup';
import { ListBase, ToggleListItem } from '@/modules/Lists/components/organisms';
import { Separator } from '@/modules/Separator';
import { PopupRef } from '@/shared/types/popup';
import { FilterPopup, FilterPopupRef } from './FilterPopup';
import { AdvancedFilterConfig } from '../../types/filter';
import { Tag } from '@/modules/Tag';
import { faCheck, faGears, faSearch } from '@fortawesome/pro-regular-svg-icons';

interface TableToolbarProps<T = unknown> extends TableToolbarConfig {
  /** Valor actual de búsqueda (controlado). Cuando se pasa, el input y los chips reflejan este valor (p. ej. restaurado desde persistencia). */
  searchValue?: string;
  selectedCount?: number;
  /** IDs de filas seleccionadas; se usa para construir el contexto pasado a los callbacks del toolbar. */
  selectedIds?: (string | number)[];
  columns?: ColumnConfig<T>[];
  rows?: RowConfig<T>[]; // Datos de la tabla para extraer valores únicos en filtros
  filters?: AdvancedFilterConfig[];
  onColumnConfig?: () => void;
  onColumnVisibilityChange?: (columnId: string, visible: boolean) => void;
  onMultipleColumnVisibilityChange?: (
    updates: Array<{ columnId: string; visible: boolean }>
  ) => void;
  onColumnReorder?: (reorderedColumns: ColumnConfig[]) => void;
  onFiltersApply?: (filters: AdvancedFilterConfig[]) => void;
  className?: string;
}

export const TableToolbar = <T = unknown, >({
  showSearch = true,
  searchPlaceholder = 'Buscar...',
  searchValue: controlledSearchValue,
  showColumnConfig = true,
  showCustomAction = false,
  customActionLabel = 'Acción personalizada',
  customActionIcon,
  showSecondCustomAction = false,
  secondCustomActionLabel = 'Segunda acción',
  secondCustomActionIcon,
  showFilters = true,
  onSearch,
  onFilter,
  onCustomAction,
  onSecondCustomAction,
  primaryCustomAction,
  secondaryCustomAction,
  customActions = [],
  selectedCount = 0,
  selectedIds = [],
  columns = [],
  rows = [],
  filters = [],
  onColumnConfig,
  onColumnVisibilityChange,
  onMultipleColumnVisibilityChange,
  onColumnReorder,
  onFiltersApply,
  className,
}: TableToolbarProps<T>) => {
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const searchQuery =
    controlledSearchValue !== undefined ? controlledSearchValue : internalSearchQuery;
  const [columnItems, setColumnItems] = useState<ToggleListItem[]>([]);
  const [tempColumnItems, setTempColumnItems] = useState<ToggleListItem[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const popupRef = useRef<PopupRef>(null);
  const filterPopupRef = useRef<FilterPopupRef>(null);

  const selectionContext: ToolbarSelectionContext = useMemo(
    () => ({
      selectedIds,
      selectedRows: rows.filter((r) => selectedIds.includes(r.id)) as RowConfig<unknown>[],
    }),
    [rows, selectedIds],
  );

  // Sincronizar columnas con items de la lista
  useEffect(() => {
    const items: ToggleListItem[] = columns.map((col) => ({
      id: col.id,
      title: typeof col.header === 'string' ? col.header : col.id,
      checked: col.visible !== false, // Por defecto visible si no se especifica
      draggable: !col.locked, // Las columnas bloqueadas no se pueden arrastrar
      disabled: col.locked, // Las columnas bloqueadas tienen icono disabled
    }));

    // Ordenar: columnas bloqueadas (disabled) primero, luego las demás
    const sortedItems = [
      ...items.filter((item) => item.disabled),
      ...items.filter((item) => !item.disabled),
    ];

    setColumnItems(sortedItems);
    // Siempre actualizar tempColumnItems cuando cambian las columnas
    // (para reflejar cambios de bloqueo externos)
    setTempColumnItems(sortedItems);
  }, [columns]);

  const handleSearch = (value: string) => {
    if (controlledSearchValue === undefined) setInternalSearchQuery(value);
    onSearch?.(value);
  };

  // Handler temporal para el toggle (no aplica cambios inmediatamente)
  const handleTempToggleChange = (id: string, checked: boolean) => {
    setTempColumnItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, checked } : item)),
    );
  };

  // Handler temporal para el reordenamiento (no aplica cambios inmediatamente)
  const handleTempItemsReorder = (reorderedItems: ToggleListItem[]) => {
    setTempColumnItems(reorderedItems);
  };

  // Aplicar cambios cuando se hace click en Guardar
  const handleSave = () => {
    // Recopilar todos los cambios de visibilidad
    const visibilityChanges: Array<{ columnId: string; visible: boolean }> = [];
    tempColumnItems.forEach((tempItem) => {
      const originalItem = columnItems.find((item) => item.id === tempItem.id);
      if (originalItem && originalItem.checked !== tempItem.checked) {
        visibilityChanges.push({ columnId: tempItem.id, visible: tempItem.checked ?? false });
      }
    });

    // Aplicar cambios de visibilidad de una vez (batch) para evitar sobreescrituras en localStorage
    if (visibilityChanges.length > 0) {
      // Si hay múltiples cambios, usar el método batch
      if (visibilityChanges.length > 1 && onMultipleColumnVisibilityChange) {
        onMultipleColumnVisibilityChange(visibilityChanges);
      } else {
        // Si solo hay un cambio o no hay método batch, llamar individualmente
        visibilityChanges.forEach((change) => {
          onColumnVisibilityChange?.(change.columnId, change.visible);
        });
      }
    }

    // Aplicar cambios de orden
    const hasOrderChanged = tempColumnItems.some(
      (item, index) => columnItems[index]?.id !== item.id,
    );

    if (hasOrderChanged) {
      // Crear un mapa de índices basado en el nuevo orden
      const orderMap = new Map(tempColumnItems.map((item, index) => [item.id, index]));

      // Reordenar las columnas según el nuevo orden
      const reorderedColumns = [...columns].sort((a, b) => {
        const indexA = orderMap.get(a.id) ?? 999;
        const indexB = orderMap.get(b.id) ?? 999;
        return indexA - indexB;
      });

      // Notificar el cambio de orden
      onColumnReorder?.(reorderedColumns as ColumnConfig[]);
    }

    // Actualizar el estado principal con los cambios temporales
    setColumnItems(tempColumnItems);

    // Marcar que el popup está cerrado
    setIsPopupOpen(false);

    // Cerrar el popup
    popupRef.current?.close();
  };

  // Cancelar cambios y cerrar el popup
  const handleCancel = () => {
    // Revertir los cambios temporales al estado original
    setTempColumnItems(columnItems);

    // Marcar que el popup está cerrado
    setIsPopupOpen(false);

    // Cerrar el popup
    popupRef.current?.close();
  };

  // Cuando se abre el popup, sincronizar el estado temporal con el actual
  const handlePopupOpen = () => {
    setIsPopupOpen(true);
    setTempColumnItems([...columnItems]); // Crear una copia para evitar mutaciones
  };

  const baseClasses = cn('ui-flex ui-flex-col ui-bg-fill', className);

  // Mapa de operadores a símbolos/texto para la descripción
  const OPERATOR_SYMBOLS: Record<string, string> = {
    equals: '=',
    notEquals: '≠',
    contains: 'contiene',
    notContains: 'no contiene',
    startsWith: 'empieza con',
    endsWith: 'termina con',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
    between: 'entre',
    isEmpty: 'está vacío',
    isNotEmpty: 'no está vacío',
    in: 'es uno de',
    afterDate: '>',
    beforeDate: '<',
    thisMonth: 'es este mes',
    lastMonth: 'es mes pasado',
    thisYear: 'es este año',
    lastYear: 'es año pasado',
  };

  // Función para obtener el label del filtro para mostrar en el tag
  const getFilterLabel = (filter: AdvancedFilterConfig): string => {
    const column = columns.find((col) => col.id === filter.columnId);
    const columnName = column
      ? typeof column.header === 'string'
        ? column.header
        : column.id
      : filter.columnId;

    // Para filtros de búsqueda global, mostrar solo el valor
    if (filter.columnId === '_search') {
      return `"${filter.value}"`;
    }

    const symbol = OPERATOR_SYMBOLS[filter.operator] || filter.operator;

    // Operadores sin valor (isEmpty, isNotEmpty, fechas relativas)
    if (
      ['isEmpty', 'isNotEmpty', 'thisMonth', 'lastMonth', 'thisYear', 'lastYear'].includes(
        filter.operator,
      )
    ) {
      return `"${columnName}" ${symbol}`;
    }

    // Operador "in" (es uno de)
    if (filter.operator === 'in' && Array.isArray(filter.value)) {
      const valuesText = (Array.isArray(filter.value) ? filter.value : [filter.value])
        .map((v) => `"${String(v)}"`)
        .join(', ');
      return `"${columnName}" ${symbol} [${valuesText}]`;
    }

    // Operador "between"
    if (filter.operator === 'between' && filter.value && filter.value2) {
      return `"${columnName}" ${symbol} "${filter.value}" y "${filter.value2}"`;
    }

    // Operadores con un valor
    if (filter.value) {
      return `"${columnName}" ${symbol} "${filter.value}"`;
    }

    return columnName;
  };

  // Función para determinar el color del tag según el tipo de filtro
  const getFilterTagColor = (
    filter: AdvancedFilterConfig,
  ): 'accent' | 'success' | 'info' | 'warning' | 'danger' | 'gray' => {
    // Filtros de búsqueda global en verde (success)
    if (filter.columnId === '_search') {
      return 'success';
    }
    // Resto de filtros en azul (accent)
    return 'accent';
  };

  // Handler para eliminar un filtro individual
  const handleRemoveFilter = (filterToRemove: AdvancedFilterConfig) => {
    const newFilters = filters.filter(
      (f) =>
        !(
          f.columnId === filterToRemove.columnId &&
          f.operator === filterToRemove.operator &&
          f.value === filterToRemove.value
        ),
    );
    onFiltersApply?.(newFilters);
  };

  // Handler para limpiar todos los filtros y búsqueda
  const handleClearAllFilters = () => {
    onFiltersApply?.([]);
    if (controlledSearchValue === undefined) setInternalSearchQuery('');
    onSearch?.('');
  };

  // Handler para limpiar solo la búsqueda
  const handleClearSearch = () => {
    if (controlledSearchValue === undefined) setInternalSearchQuery('');
    onSearch?.('');
  };

  // Verificar si hay filtros activos (incluyendo búsqueda)
  const hasActiveFilters = filters.length > 0 || searchQuery.trim().length > 0;

  // Calcular el total de filtros activos (incluyendo búsqueda)
  const totalActiveFilters = filters.length + (searchQuery.trim().length > 0 ? 1 : 0);

  return (
    <div className={baseClasses}>
      {/* Header del toolbar */}
      <div
        className={cn(
          'ui-flex ui-items-center ui-justify-between ui-p-16',
          hasActiveFilters && 'ui-border-b ui-border-default',
        )}
      >
        {/* Lado izquierdo: Configuración, divisor, búsqueda y filtro */}
        <div className="ui-flex ui-items-center ui-gap-8">
          {/* Botón de configuración de tabla */}
          {showColumnConfig && (
            <>
              <Popup
                ref={popupRef}
                className="ui-flex ui-min-w-[300px] ui-flex-col ui-gap-y-8 ui-p-8"
                theme="solid"
                color="tertiary"
                position="bottom-left"
                icon={faGears}
                size="xs"
                absolute
                closeOnClick={false}
                tooltip={{ label: 'Configuración de tabla', position: 'top-left' }}
                onOpen={handlePopupOpen}
                tooltipProps={{ label: 'Configuración de tabla', position: 'top-left' }}
              >
                <h1 className="ui-text-body-sm-regular ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm">
                  Configuración de tabla
                </h1>

                <Separator className="ui-w-full" />

                <ListBase
                  variant="toggle"
                  items={tempColumnItems}
                  onToggleChange={handleTempToggleChange}
                  onItemsReorder={handleTempItemsReorder}
                  enableReordering={true}
                />

                <Separator className="ui-w-full" />

                <div className="ui-flex ui-justify-center ui-gap-x-8">
                  <InnerButton
                    fullWidth
                    theme="solid"
                    color="secondary"
                    size="xxs"
                    pill="md"
                    onClick={handleCancel}
                  >
                    Cancelar
                  </InnerButton>
                  <InnerButton
                    fullWidth
                    theme="solid"
                    color="primary"
                    size="xxs"
                    pill="md"
                    iconProps={{ icon: faCheck }}
                    onClick={handleSave}
                  >
                    Guardar
                  </InnerButton>
                </div>
              </Popup>

              {/* Divisor vertical */}
              <div className="ui-h-control-xs ui-border-1 ui-border-default" />
            </>
          )}

          {/* Input de búsqueda */}
          {showSearch && (
            <div className="ui-w-[223px]">
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onValueChange={handleSearch}
                leftSlot={faSearch}
                size="xs"
                fullWidth
              />
            </div>
          )}

          {/* Botón de filtro avanzado */}
          {showFilters && (
            <div className="ui-relative">
              <FilterPopup
                ref={filterPopupRef}
                columns={columns as ColumnConfig<unknown>[]}
                rows={rows as RowConfig<unknown>[]}
                filters={filters}
                onFiltersApply={(newFilters) => {
                  onFiltersApply?.(newFilters);
                }}
              />
              {/* Badge con número de filtros activos (incluyendo búsqueda) */}
              {totalActiveFilters > 0 && (
                <div className="ui-absolute ui--right-1 ui--top-1 ui-flex ui-size-16 ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-fill-danger ui-text-body-xs-regular ui-font-body-xs-bold ui-text-on-color">
                  {totalActiveFilters}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Lado derecho: customActions → secondary → primary */}
        <div className="ui-flex ui-items-center ui-gap-8">
          {/* customActions: siempre antes del secondary */}
          {customActions.map((action, index) => (
            <InnerButton
              key={index}
              theme={action.theme ?? 'solid'}
              color={action.color ?? 'secondary'}
              size={action.size ?? 'xs'}
              pill="sm"
              leftSlot={action.leftSlot}
              onClick={() => action.onClick(selectionContext)}
              disabled={action.disabled}
            >
              {action.label}
            </InnerButton>
          ))}

          {/* Secondary: nueva API o legacy */}
          {secondaryCustomAction !== null && secondaryCustomAction !== undefined ? (
            <InnerButton
              theme={secondaryCustomAction.theme ?? 'solid'}
              color={secondaryCustomAction.color ?? 'secondary'}
              size={secondaryCustomAction.size ?? 'xs'}
              pill="sm"
              leftSlot={secondaryCustomAction.leftSlot}
              onClick={() => secondaryCustomAction.onClick(selectionContext)}
              disabled={secondaryCustomAction.requiresSelection !== false && selectedCount === 0}
            >
              {secondaryCustomAction.label}
            </InnerButton>
          ) : (
            showSecondCustomAction && (
              <InnerButton
                theme="solid"
                color="secondary"
                size="xs"
                pill="sm"
                leftSlot={secondCustomActionIcon}
                onClick={() => onSecondCustomAction?.(selectionContext)}
                disabled={selectedCount === 0}
              >
                {secondCustomActionLabel}
              </InnerButton>
            )
          )}

          {/* Primary: nueva API o legacy */}
          {primaryCustomAction !== null && primaryCustomAction !== undefined ? (
            <InnerButton
              theme={primaryCustomAction.theme ?? 'solid'}
              color={primaryCustomAction.color ?? 'primary'}
              size={primaryCustomAction.size ?? 'xs'}
              pill="sm"
              leftSlot={primaryCustomAction.leftSlot}
              onClick={() => primaryCustomAction.onClick(selectionContext)}
              disabled={primaryCustomAction.requiresSelection !== false && selectedCount === 0}
            >
              {primaryCustomAction.label}
            </InnerButton>
          ) : (
            showCustomAction && (
              <InnerButton
                theme="solid"
                color="primary"
                size="xs"
                pill="sm"
                leftSlot={customActionIcon}
                onClick={() => onCustomAction?.(selectionContext)}
                disabled={selectedCount === 0}
              >
                {customActionLabel}
              </InnerButton>
            )
          )}
        </div>
      </div>

      {/* Sección de filtros activos - Solo se muestra si hay filtros o búsqueda activa */}
      {hasActiveFilters && (
        <div className="ui-flex ui-flex-col ui-gap-8 ui-bg-fill ui-px-16 ui-py-8">
          {/* Texto "Filtros activos:" y "Limpiar todos" */}
          <div className="ui-flex ui-items-center ui-gap-8">
            <span className="ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-tertiary">
              Filtros activos:
            </span>
            <Button color="link" size="xxs" onClick={handleClearAllFilters}>
              Limpiar todos
            </Button>
          </div>

          {/* Tags con los filtros aplicados */}
          <div className="ui-flex ui-flex-wrap ui-items-center ui-gap-8">
            {/* Tag de búsqueda (verde/success) - Se muestra primero */}
            {searchQuery.trim().length > 0 && (
              <Tag
                key="_search"
                label={`"${searchQuery}"`}
                color="success"
                infoIcon={{ name: faSearch }}
                onClose={handleClearSearch}
              />
            )}

            {/* Tags de filtros avanzados (azul/accent) */}
            {filters.map((filter, index) => (
              <Tag
                key={`${filter.columnId}-${filter.operator}-${index}`}
                label={getFilterLabel(filter)}
                color={getFilterTagColor(filter)}
                onClose={() => handleRemoveFilter(filter)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
